import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
  FaStar,
  FaExternalLinkAlt
} from 'react-icons/fa';
import programMapsData from './programMapsData.json';
import { Link } from "react-router-dom";


/**
 * Check if a term has any "issues":
 * - Has an alertMessage, or
 * - Has at least one highlighted course
 */
function termHasAlert(termObj) {
  if (termObj.alertMessage) return true;
  return termObj.courses.some((course) => course.highlight);
}

/**
 * Return an icon for a term that has an issue.
 * If the term is fine, return nothing.
 */
function getTermIcon(hasAlert) {
  if (hasAlert) {
    return (
      <FaExclamationTriangle className="text-red-500 mr-2 text-lg" />
    );
  }
  return null;
}

/**
 * Return an icon next to a course if it's highlighted.
 */
function getCourseIcon(course) {
  if (course.highlight) {
    return (
      <FaExclamationTriangle className="text-red-500 mr-2 inline-block" />
    );
  }
  return null;
}

/**
 * ExpandableTerm component animates the expanding and collapsing
 * of a term's detailed content.
 */
const ExpandableTerm = ({
  termObj,
  yearIndex,
  termIndex,
  toggleTerm,
  isExpanded,
  yearNumber // new prop added
}) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  // Measure content height whenever expansion state changes.
  useLayoutEffect(() => {
    if (contentRef.current) {
      setHeight(isExpanded ? contentRef.current.scrollHeight : 0);
    }
  }, [isExpanded, termObj]);

  const hasAlert = termHasAlert(termObj);

  return (
    <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
      {/* Term Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
          hasAlert ? 'bg-red-50 hover:bg-red-100' : 'bg-white hover:bg-gray-50'
        }`}
        onClick={() => toggleTerm(yearIndex, termIndex)}
      >
        {/* Left side: Icon (if needed) + heading */}
        <div className="flex items-center space-x-2">
          {getTermIcon(hasAlert)}
          <div>
          <div className="text-lg text-gray-800">
            <span className="font-semibold">
              Year {yearNumber}, {termObj.term}
            </span>
            <span className="ml-2">
              {termObj.requirementsCount} Requirements ({termObj.totalCredits} Credits)
            </span>
          </div>
            <div className="text-sm text-gray-500">
              Term Credit Ratio: {termObj.termCreditRatio}% ({termObj.creditsAtRisk} Credits)
              <a
                href="#"
                className="ml-3 text-blue-600 hover:underline inline-flex items-center"
                onClick={(e) => e.stopPropagation()} // Prevent toggling on link click
              >
                Open in Curriculum
                <FaExternalLinkAlt className="ml-1" />
              </a>
            </div>
          </div>
        </div>
        {/* Right side: expand/collapse arrow */}
        <div className="ml-2">
          {isExpanded ? (
            <FaChevronUp className="text-gray-600" />
          ) : (
            <FaChevronDown className="text-gray-600" />
          )}
        </div>
      </div>

      {/* Expandable content */}
      <div
        ref={contentRef}
        style={{ height: `${height}px`, transition: 'height 0.5s ease' }}
        className="overflow-hidden bg-white px-4"
      >
        <div className="pb-4 pt-2">
          <ul className="mt-2">
            {termObj.courses.map((course, cIdx) => (
              <li
                key={cIdx}
                className={`flex items-center justify-between p-2 hover:bg-gray-50 border-b border-gray-200 last:border-0 ${
                  course.highlight ? 'bg-red-50' : ''
                }`}
              >
                <div className="flex items-center">
                  {getCourseIcon(course)}
                  <span
                    className={`text-sm font-medium ${
                      course.highlight ? 'text-red-800' : 'text-gray-800'
                    }`}
                  >
                    {course.courseCode} - {course.courseTitle}
                  </span>
                </div>
                <span className="text-sm text-gray-600">{course.credits} cr</span>
              </li>
            ))}
          </ul>

          {/* Alert message if present */}
          {termObj.alertMessage && (
            <div className="flex items-center mt-4 bg-red-50 border border-red-200 p-3 text-sm text-red-800 rounded">
              <FaExclamationTriangle className="text-red-600 mr-2" />
              <div className="flex-1">{termObj.alertMessage}</div>
              <Link
                to="/scheduling-analytics/CMPT%20410"
                className="text-blue-600 hover:underline inline-flex items-center"
              >
                Open Courses Analysis
                <FaExternalLinkAlt className="ml-1" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProgramMaps = ({ program }) => {
  const [maps, setMaps] = useState([]);
  const [selectedMapIndex, setSelectedMapIndex] = useState(0);

  // Track expanded state for each (yearIndex, termIndex)
  const [expandedTerm, setExpandedTerm] = useState({});

  useEffect(() => {
    if (program && program.programCode) {
      const found = programMapsData.find(
        (item) => item.programCode === program.programCode
      );
      if (found && found.maps) {
        setMaps(found.maps);
        setSelectedMapIndex(0);
      } else {
        setMaps([]);
      }
    }
  }, [program]);

  const toggleTerm = (yearIndex, termIndex) => {
    setExpandedTerm((prev) => {
      const key = `${yearIndex}-${termIndex}`;
      return {
        ...prev,
        [key]: !prev[key]
      };
    });
  };

  if (!program) {
    return null;
  }

  // If no maps are available, just show a message
  if (maps.length === 0) {
    return (
      <div className="mt-6">
        <p className="text-gray-600 text-sm">
          No program map data found for <strong>{program.programTitle}</strong>.
        </p>
      </div>
    );
  }

  // The user can choose which map to display
  const currentMap = maps[selectedMapIndex];

  return (
    <div className="mt-3">
      {/* Top bar: "Program Map" + dropdown + "Act on recommendations" button */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-base font-semibold text-gray-700">Program Map</h2>
          {/* If there's more than one map, show dropdown */}
          {maps.length > 1 ? (
            <select
              className="text-sm border border-gray-300 rounded px-2 py-1 min-w-[20vw]"
              value={selectedMapIndex}
              onChange={(e) => setSelectedMapIndex(Number(e.target.value))}
            >
              {maps.map((mapItem, idx) => (
                <option key={mapItem.mapId} value={idx}>
                  {mapItem.mapName}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-gray-600">
              {currentMap.mapName}
            </span>
          )}
        </div>

        {/* Act on recommendations button (light blue style + star icon) */}
        <button
          className="flex items-center text-sm text-blue-600 border border-blue-600 px-2 py-1 rounded hover:bg-blue-50"
        >
          <FaStar className="mr-1" />
          Act on recommendations to improve the effectiveness of your program map
        </button>
      </div>

      {/* MAP TITLE + DESCRIPTION */}
      <div className="mb-4 bg-gray-50 p-3 border border-gray-200 rounded">
        <h4 className="text-md font-bold text-gray-800">
          {currentMap.mapName}
        </h4>
        {currentMap.description && (
          <p className="text-sm text-gray-600">{currentMap.description}</p>
        )}
      </div>

      {/* RENDER YEARS/TERMS */}
      {currentMap.years.map((yearObj, yearIndex) => (
        <div key={yearIndex} className="mb-3">
          {yearObj.terms.map((termObj, termIndex) => {
            const key = `${yearIndex}-${termIndex}`;
            const isExpanded = expandedTerm[key] || false;

            return (
              <ExpandableTerm
                key={termIndex}
                termObj={termObj}
                yearIndex={yearIndex}
                termIndex={termIndex}
                toggleTerm={toggleTerm}
                isExpanded={isExpanded}
                yearNumber={yearObj.yearNumber} // pass the year number here
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default ProgramMaps;
